import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { bodyCellStyles, STUDENT_NAME_COLOR } from "./proficiencyTable";

// A cell's identity is its STATUS, never its numeric level: math_level is null
// for every work-in-progress state, so keying the lookup on it renders every
// WIP cell as no data (D41.6).
//
// The firm levels keep the exact fill, colours and warning icon they have
// today. The work-in-progress ones are text-and-border only, in the student-name
// colour: no colour code, yet never mistaken for a firm level at a glance.
const WORK_IN_PROGRESS = { label: "WIP", color: STUDENT_NAME_COLOR, wip: true };

export const STATUS_CONFIG = {
  LEVEL_1: {
    label: "Level 1",
    color: colors.pink800,
    bg: colors.pink50,
    border: colors.pink300,
    warn: true,
  },
  LEVEL_2: {
    label: "Level 2",
    color: "#9B6B03",
    bg: "#FEFCE8",
    border: "#EAB308",
  },
  LEVEL_3: {
    label: "Level 3",
    color: colors.green700,
    bg: colors.green50,
    border: "#76D9AD",
  },
  WIP_HIGH: WORK_IN_PROGRESS,
  WIP_MID: WORK_IN_PROGRESS,
  WIP_LOW: WORK_IN_PROGRESS,
};

const formatSessionDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const EvidenceLine = ({ label, value }) => (
  <Line data-testid="evidence-line">
    <LineLabel>{label}:</LineLabel> {value}
  </Line>
);

const EvidenceTip = ({ entry }) => (
  <>
    <EvidenceLine
      label="Solo Accuracy Last 5"
      value={`${entry.solo_correct}/${entry.solo_attempts}`}
    />
    {entry.most_recent_date && (
      <EvidenceLine
        label="Most Recent"
        value={`${entry.most_recent_correct}/${entry.most_recent_attempts}; ${formatSessionDate(entry.most_recent_date)}`}
      />
    )}
    {entry.total_attempts > 0 && (
      <EvidenceLine
        label="Total Solo Accuracy"
        value={`${entry.total_correct}/${entry.total_attempts}`}
      />
    )}
  </>
);

export const ProficiencyCell = ({
  entry,
  studentId,
  studentName,
  standardCode,
  onCellClick,
}) => {
  const code = standardCode.split(":")[0].trim();
  const config = entry && STATUS_CONFIG[entry.status];

  if (!config) {
    // "No evidence yet" said out loud: a blank would read as a broken page and
    // a zero as a failure the student did not earn (D41.7).
    return (
      <Cell aria-label={`${studentName}, ${code}: no data`}>
        <span aria-hidden="true">—</span>
      </Cell>
    );
  }

  const evidenceId = `proficiency-evidence-${studentId}-${code}`;

  return (
    <Cell>
      <Tooltip
        id={evidenceId}
        tip={<EvidenceTip entry={entry} />}
        placement="right"
        nowrap
      >
        <Pill
          as="button"
          type="button"
          aria-label={`${studentName}, ${code}: ${config.label}. View session report`}
          aria-describedby={evidenceId}
          $color={config.color}
          $bg={config.wip ? "transparent" : config.bg}
          $border={config.wip ? config.color : config.border}
          $clickable
          onClick={() => onCellClick({ studentId, standardCode: code })}
        >
          {config.warn && (
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 14 }} />
          )}
          {config.label}
        </Pill>
      </Tooltip>
    </Cell>
  );
};

const Cell = styled.td`
  ${bodyCellStyles}
  background: ${colors.gray50};
  width: 1px;
  white-space: nowrap;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: inherit;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border-radius: 11px;
  padding: 4px 8px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  border: ${(p) => (p.$border ? `1px solid ${p.$border}` : "none")};

  &:hover {
    ${(p) => p.$clickable && "opacity: 0.8;"}
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;

const Line = styled.span`
  display: block;

  & + & {
    margin-top: 8px;
  }
`;

const LineLabel = styled.span`
  font-weight: 700;
`;
